import { ApolloError } from '@apollo/client';

import {
  FundingIntakeInput,
  useErpConfirmFundingIntakeMutation,
  useErpFundingWatchHealthQuery,
  useErpFundingFormSubmissionQuery,
  useErpFundingFormSubmissionsQuery,
  useErpFundingFormSubmissionsTotalQuery,
  useErpFundingSubmissionStatesQuery,
  useErpFundingImportRunQuery,
  useErpFundingImportRunsQuery,
  useErpFundingIntakeQuery,
  useErpFundingNotificationsQuery,
  useErpRetryFormSubmissionMutation,
  useErpReviewOperatorPageMutation,
  useErpFundingTaskSummaryQuery,
  useErpRunFundingTaskMutation,
} from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import {
  IFormSubmission,
  IFundingNotification,
  IFundingTaskRun,
  IFundingTaskSummary,
} from './dofinansowanieModel';
import { useDofinPermissions } from './useDofinPermissions';

/**
 * ТЗ 0045 — то, что делает робот, и как в это вмешаться.
 *
 * Стабильные пустые массивы: свежий `[]` на каждый рендер перезапускает все
 * зависимые `useMemo`/`useEffect` (та же заметка, что в `useFunding`).
 */
const EMPTY_NOTIFICATIONS: IFundingNotification[] = [];
const EMPTY_RUNS: IFundingTaskRun[] = [];
const EMPTY_SUMMARY: IFundingTaskSummary[] = [];

const EMPTY_SUBMISSIONS: IFormSubmission[] = [];
const EMPTY_HEALTH: IWatchHealthRow[] = [];

/** Строка сводки «что мешает роботу следить». */
export interface IWatchHealthRow {
  /** Чинится по-разному, поэтому причины разные: `no_url` / `error` /
   *  `blind` / `never` / `url_suspect`. */
  problem: string;
  count: number;
  /** Имена, а не id: строку читают глазами. Не больше 50. */
  operators: string[];
}

export interface INotificationFilters {
  candidateId?: string | null;
  kind?: string;
  status?: string;
  deliveryState?: string;
  operatorId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  limit?: number;
  offset?: number;
  /** Письма про КОНКРЕТНОЕ окно набора — для его карточки. */
  intakeId?: string;
  /** ТЗ 0085: письма одного правила — ссылка «отправлено по правилу». */
  ruleId?: string;
  /** ТЗ 0085: письма одного шаблона SendGrid. */
  templateId?: string;
}

/**
 * Журнал писем домена.
 *
 * Без `candidateId` — ОБЩИЙ список: раньше это было невозможно, у запроса id
 * был обязательным, и вопрос «кому мы вообще писали» через API не задавался.
 * Счётчик приходит тем же запросом и считается тем же фильтром — разъехавшись,
 * они показали бы «найдено 240» над списком из другого набора строк.
 */
/**
 * Одно окно набора — для его карточки.
 *
 * 🔴 Карточки набора не существовало: окно жило строкой в таблице, строкой
 * внутри карточки оператора и третьим местом — панелью рассылки внизу
 * вкладки. Три поверхности про одну сущность, и ни на одной не было видно
 * ни фазы окна, ни того, кому уже писали.
 */
export const useFundingIntake = (intakeId?: string | null) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingIntakeQuery({
    variables: { id: intakeId || '' },
    skip: !canRead || !intakeId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    intake: data?.fundingIntake ?? null,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

export const useFundingNotifications = (filters: INotificationFilters = {}) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingNotificationsQuery({
    variables: {
      candidateId: filters.candidateId || undefined,
      kind: filters.kind || undefined,
      status: filters.status || undefined,
      deliveryState: filters.deliveryState || undefined,
      operatorId: filters.operatorId || undefined,
      intakeId: filters.intakeId || undefined,
      ruleId: filters.ruleId || undefined,
      templateId: filters.templateId || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      search: filters.search || undefined,
      limit: filters.limit ?? 50,
      offset: filters.offset ?? 0,
    },
    // `PermissionDenied` летит в `errors`, то есть ОТКЛОНЯЕТ промис, а не
    // возвращает `customErrors` — роль без доступа обязана пропустить запрос.
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  return {
    notifications: (data?.fundingNotifications ??
      EMPTY_NOTIFICATIONS) as IFundingNotification[],
    total: data?.fundingNotificationsTotal ?? 0,
    loading,
    /** Ответ есть (или его не будет: нет прав, отказ) — нули настоящие. */
    settled: !canRead || Boolean(data) || Boolean(error),
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

/** Заявки с публичной формы, включая те, чей авторазбор упал. */
/**
 * Сколько всего заявок под тем же фильтром.
 *
 * 🔴 Экран писал длину страницы (`limit: 100`) как «сколько заявок», без
 * единого намёка на обрезку. Публичная форма набирает их бесконечно.
 */
/** Заявок на страницу. Пагинация, а не «первые сто с плашкой»: форма
    набирает заявки бесконечно, и историю надо листать (ТЗ 0050). */
export const SUBMISSIONS_PAGE_SIZE = 50;

/**
 * Фильтры вкладки заявок.
 *
 * ⚠️ Один объект на список и на счётчик. Разойдись они хоть на один
 * аргумент — таблица и число в шапке начнут отвечать на разные вопросы
 * (`counter-must-match-the-button`).
 */
export interface ISubmissionFilters {
  /** failed | pending | needs_operator | linked | rejected. */
  state?: string;
  search?: string;
  /** Обе границы ВКЛЮЧИТЕЛЬНЫ; формат — `YYYY-MM-DD`, как отдаёт `input[date]`. */
  submittedFrom?: string;
  submittedTo?: string;
}

const submissionVars = (filters: ISubmissionFilters) => ({
  state: filters.state || undefined,
  search: filters.search || undefined,
  submittedFrom: filters.submittedFrom || undefined,
  submittedTo: filters.submittedTo || undefined,
});

export const useFundingFormSubmissionsTotal = (
  filters: ISubmissionFilters = {},
) => {
  const { canRead } = useDofinPermissions();
  const { data, refetch } = useErpFundingFormSubmissionsTotalQuery({
    variables: submissionVars(filters),
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  return {
    total: data?.fundingFormSubmissionsTotal ?? null,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * Счётчики плиток над списком заявок.
 *
 * ⚠️ Те же остальные фильтры, что и у списка: плитка — это кнопка, и она
 * обязана приводить ровно к обещанному числу
 * (`counter-must-match-the-button`). Состояние в фильтры НЕ передаём — это
 * собственное измерение плиток, и выбор одной обнулил бы остальные.
 */
export const useFundingSubmissionStates = (
  filters: ISubmissionFilters = {},
) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, refetch } = useErpFundingSubmissionStatesQuery({
    variables: {
      search: filters.search || undefined,
      submittedFrom: filters.submittedFrom || undefined,
      submittedTo: filters.submittedTo || undefined,
    },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  const counts: Record<string, number> = {};
  (data?.fundingSubmissionStates ?? []).forEach((row) => {
    counts[row.state] = row.count;
  });

  // 🔴 `refetch` наружу обязателен. Разбор в окне меняет состояние заявки, и
  // без него строка уезжала в «В работе», а плитки продолжали показывать
  // «Нужен оператор: 5» — то есть кнопка обещала одно, а приводила к другому.
  // Поймано в браузере, гейтами не видно.
  return { counts, loading, refetch: useStableRefetch(refetch) };
};

/** Одна заявка для окна разбора: ответы формы и происхождение оператора. */
export const useFundingFormSubmission = (id?: string | null) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingFormSubmissionQuery({
    variables: { id: id || '' },
    skip: !canRead || !id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    submission: (data?.fundingFormSubmission ?? null) as IFormSubmission | null,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

export const useFundingFormSubmissions = (
  filters: ISubmissionFilters = {},
  offset = 0,
) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingFormSubmissionsQuery({
    variables: {
      ...submissionVars(filters),
      limit: SUBMISSIONS_PAGE_SIZE,
      offset,
    },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    submissions: (data?.fundingFormSubmissions ??
      EMPTY_SUBMISSIONS) as IFormSubmission[],
    // Адрес публичной формы едет тем же ответом (ТЗ 0061): менеджеру его
    // нужно дать человеку, а зашитый во фронт он разъехался бы с тем, что
    // робот ставит в письмо.
    formUrl: data?.fundingFormUrl ?? '',
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

/** «Разобрать заново» для заявки, чей автоматический разбор упал. */
export const useRetryFormSubmission = () => {
  const [mutate, state] = useErpRetryFormSubmissionMutation();

  const retry = async (id: string) => {
    try {
      const { data } = await mutate({ variables: { id } });
      return data?.retryFormSubmission ?? null;
    } catch {
      return null;
    }
  };

  return { retry, retrying: state.loading };
};

/**
 * Журнал прогонов фоновых задач домена.
 *
 * `total` приезжает тем же запросом, что и страница: без него экран печатал
 * длину страницы и читался как «прогонов всего 25» — а пачка ручных прогонов
 * выдавила бы из этого окна всю ночную серию, то есть сломала бы ровно тот
 * вопрос, ради которого журнал заведён.
 */
export interface IRunFilters {
  taskName?: string;
  status?: string;
  triggerSource?: string;
  dryRun?: boolean;
}

export const useFundingTaskRuns = (
  filters: IRunFilters = {},
  limit = 20,
  offset = 0,
) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingImportRunsQuery({
    // Фильтры уходят и в список, и в счётчик ОДНИМ набором — иначе
    // «показано 25 из 40» отвечало бы на другой вопрос, чем сама таблица.
    variables: { limit, offset, ...filters },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  return {
    runs: (data?.fundingImportRuns ?? EMPTY_RUNS) as IFundingTaskRun[],
    total: data?.fundingImportRunsTotal ?? 0,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * Сводка «ходят ли задачи» — по строке на задачу.
 *
 * Отдельный запрос, а не выборка из списка прогонов: десяток ручных запусков
 * одной задачи вытесняет остальные из окна, и сводка соврала бы «не ходила
 * ни разу» про задачу, которая ходит каждую ночь.
 */
export const useFundingTaskSummary = () => {
  const { canRead } = useDofinPermissions();
  const { data, loading, refetch } = useErpFundingTaskSummaryQuery({
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  return {
    summary: (data?.fundingTaskSummary ??
      EMPTY_SUMMARY) as IFundingTaskSummary[],
    loading,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * Что мешает роботу следить — сводка для журнала прогонов (ТЗ 0054).
 *
 * 🔴 Живёт ЗДЕСЬ, а не в реестре операторов. Владелец: «никаких ошибок в
 * интерфейсе видеть не хочу» — поломки убраны с глаз менеджера, но не из
 * системы: без этой сводки оператор без адреса выглядит ровно как спокойный,
 * и так может стоять годами.
 */
export const useFundingWatchHealth = () => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingWatchHealthQuery({
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });

  return {
    health: (data?.fundingWatchHealth ?? EMPTY_HEALTH) as IWatchHealthRow[],
    // Считает СЕРВЕР: сложить строки выше нельзя — причины пересекаются.
    brokenOperators: data?.fundingWatchOperatorsBroken ?? 0,
    loading,
    /** Ответ есть (или его не будет: нет прав, отказ) — нули настоящие. */
    settled: !canRead || Boolean(data) || Boolean(error),
    refetch: useStableRefetch(refetch),
  };
};

/** Один прогон — для карточки. */
export const useFundingRun = (id?: string | null) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error } = useErpFundingImportRunQuery({
    variables: { id: id || '' },
    skip: !canRead || !id,
    fetchPolicy: 'cache-and-network',
  });

  return {
    run: (data?.fundingImportRun ?? null) as IFundingTaskRun | null,
    loading,
    error: error as ApolloError | undefined,
  };
};

/** Запустить фоновую задачу робота по кнопке. */
export const useRunFundingTask = () => {
  const [mutate, state] = useErpRunFundingTaskMutation();

  /**
   * → `queued` | `no-worker` | `failed`.
   *
   * «Поставлено в очередь» и «будет выполнено» — разные новости: при
   * остановленном воркере задача ляжет в очередь и не будет взята никогда.
   * Молчать об этом значило бы выдать очередь за результат.
   */
  const start = async (task: string, dryRun: boolean) => {
    // 🔴 try/catch обязателен, как и у соседей в этом файле: отказ приходит
    // в GraphQL `errors`, то есть промис Apollo ОТКЛОНЯЕТСЯ. Без обёртки
    // недоступный брокер или снятые права дают необработанное исключение в
    // обработчике клика — человек подтвердил запуск и не увидел ничего.
    try {
      const { data } = await mutate({ variables: { task, dryRun } });
      const payload = data?.runFundingTask;
      if (payload?.success !== true) return 'failed';
      return payload.workerOnline ? 'queued' : 'no-worker';
    } catch {
      return 'failed';
    }
  };

  return { start, starting: state.loading };
};

/**
 * Две кнопки в карточке оператора.
 *
 * `PermissionDenied` прилетает в GraphQL `errors`, то есть промис Apollo
 * ОТКЛОНЯЕТСЯ — поэтому каждый вызов обёрнут в try/catch, иначе роль без
 * прав валила бы экран необработанным исключением.
 */
export const useOperatorPageActions = () => {
  const [reviewMutation, reviewState] = useErpReviewOperatorPageMutation();
  const [confirmMutation, confirmState] = useErpConfirmFundingIntakeMutation();

  /**
   * «Посмотрел» — один поступок и один эффект: снять флаг «страница
   * обновилась» (ТЗ 0054).
   *
   * 🔴 Здесь был второй аргумент `automationStatus`, и из-за него у кнопки
   * выросли четыре варианта — «есть набор», «набора нет», «надо править
   * карточку». Владелец: «убери это всё, не понимаю, что это». Разрешение на
   * рассылку переехало на само окно набора.
   *
   * `intake` остался: «посмотрел и записал увиденный набор» — один поход
   * менеджера и одна транзакция. Двумя запросами такого не собрать: между
   * ними помещается отказ, и остаётся либо снятый флаг без набора, либо
   * набор без снятого флага.
   */
  const reviewPage = async (
    operatorId: string,
    intake?: FundingIntakeInput,
  ) => {
    try {
      const { data } = await reviewMutation({
        variables: { operatorId, intake },
        // Реестр операторов и вкладка «Наборы» читают одни и те же строки.
        // Разбор, меняющий только карточку, оставил бы обоих врать.
        refetchQueries: [
          'ErpFundingOperators',
          'ErpFundingIntakes',
          'ErpFundingIntakesTotal',
          'ErpFundingBoard',
        ],
      });
      return data?.reviewOperatorPage ?? null;
    } catch {
      return null;
    }
  };

  /**
   * Подтвердить набор или снять подтверждение — из строки, где формы нет.
   *
   * ⚠️ `refetchQueries` обязателен и шире, чем кажется: подтверждение меняет
   * колонку реестра, счётчик сегмента «не подтверждён» и список «наборы на
   * днях». Обновив одну карточку, мы оставили бы остальные три врать —
   * ровно то, что уже ловили на плитках разбора заявок.
   */
  const confirmIntake = async (id: string, confirmed: boolean) => {
    try {
      const { data } = await confirmMutation({
        variables: { id, confirmed },
        refetchQueries: [
          'ErpFundingOperators',
          'ErpFundingOperatorsTotal',
          'ErpFundingOperator',
          'ErpFundingIntakes',
          'ErpFundingIntakesTotal',
        ],
      });
      return data?.confirmFundingIntake ?? null;
    } catch {
      return null;
    }
  };

  return {
    reviewPage,
    confirmIntake,
    reviewing: reviewState.loading,
    confirming: confirmState.loading,
  };
};
